import React, { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { UploadSimple, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

// Image input: drag & drop / click to upload, or paste a URL. Returns final URL via onChange.
export const ImageUpload = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [mode, setMode] = useState("upload");

  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(fileUrl(data.path));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="image-upload">
      <div className="mb-3 flex gap-4">
        <button type="button" onClick={() => setMode("upload")}
          className={`text-[10px] uppercase tracking-[0.25em] ${mode === "upload" ? "text-gold" : "text-[#52525b]"}`}>
          Upload
        </button>
        <button type="button" onClick={() => setMode("url")}
          className={`text-[10px] uppercase tracking-[0.25em] ${mode === "url" ? "text-gold" : "text-[#52525b]"}`}>
          Paste URL
        </button>
      </div>

      {mode === "upload" ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          data-testid="image-dropzone"
          className={`flex aspect-[4/3] cursor-pointer flex-col items-center justify-center border border-dashed transition-colors ${drag ? "border-gold bg-white/5" : "border-white/25"}`}
        >
          {value ? (
            <img src={value} alt="preview" className="h-full w-full object-cover" />
          ) : (
            <>
              <UploadSimple size={28} className="text-[#52525b]" />
              <p className="mt-3 text-xs font-light text-[#a1a1aa]">{busy ? "Uploading…" : "Drag & drop or click to upload"}</p>
            </>
          )}
          <input ref={inputRef} type="file" accept="image/*" className="hidden"
            data-testid="image-file-input" onChange={(e) => doUpload(e.target.files?.[0])} />
        </div>
      ) : (
        <div className="flex items-center gap-2 border-b border-white/20">
          <LinkSimple size={16} className="text-[#52525b]" />
          <input
            data-testid="image-url-input"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…"
            className="w-full bg-transparent py-3 text-sm outline-none placeholder:text-[#52525b]"
          />
        </div>
      )}
      {mode === "url" && value && <img src={value} alt="preview" className="mt-3 aspect-[4/3] w-full object-cover" />}
    </div>
  );
};
